import { ThemeEvents } from '@theme/events';

/**
 * Shared cart line-item property engine.
 *
 * Several PDP blocks want to push a short, customer-visible fact into the cart
 * line so it survives into the cart drawer, checkout, the order confirmation
 * email, and the Shopify admin order summary:
 *
 *   - `aaa-availability-line`  -> "Lead time: Made to order - typically 10 weeks"
 *   - `aaa-shipping-duty-prompt` (pickup-only mode)
 *                              -> "Delivery: In-store collection only - Thurles, Co. Tipperary"
 *
 * Rather than each block shipping its own copy of the DOM plumbing, they both
 * drive this one function. A host element declares what it wants via data
 * attributes and calls `syncLineProperty(host)`:
 *
 *   data-product-id       the product the host belongs to (form scoping)
 *   data-attach-property  "true" to write the input, anything else to remove it
 *   data-property-key     the label the customer sees in the cart, e.g. "Delivery"
 *   data-property-value   the plain-text value
 *
 * The key must NOT start with an underscore - Shopify hides `_`-prefixed
 * properties from the customer, and `snippets/cart-products.liquid` filters
 * them out of the cart line as well.
 *
 * Blocks that only need the property (no other behaviour) can simply use the
 * `<aaa-line-property>` element defined at the bottom of this file as their
 * root. Blocks with their own custom element (availability line, which also
 * morphs itself on variant change) import `syncLineProperty` directly.
 */

/**
 * Resolves the section-ish ancestor used to scope form matching.
 *
 * @param {HTMLElement} host
 * @returns {Element | null}
 */
export function closestSection(host) {
  return host.closest('.shopify-section, dialog');
}

/**
 * Writes (or removes) the `properties[Key]` hidden input on every product form
 * on the page that belongs to this host's product.
 *
 * Scoping rule (kept deliberately identical to the original availability-line
 * implementation): a form belongs to us if it sits inside the same section as
 * the host, or if its product/variant id input references our product id. That
 * keeps quick-add forms for OTHER products (related products, recommendations)
 * out of scope. Horizon's sticky add-to-cart submits the main product form
 * rather than one of its own, so it inherits the property with no extra work.
 *
 * Idempotent: an existing input for the same key is updated in place, never
 * duplicated, so repeated calls on variant change are safe.
 *
 * @param {HTMLElement} host - Element carrying the data-* contract above.
 * @param {Element | null} [section] - Optional pre-resolved section ancestor.
 */
export function syncLineProperty(host, section = closestSection(host)) {
  const key = host.dataset.propertyKey;
  const value = host.dataset.propertyValue;
  const productId = host.dataset.productId;
  const attach = host.dataset.attachProperty === 'true';
  if (!key) return;

  const inputName = `properties[${key}]`;
  const inputSel = `input[name="${inputName.replace(/"/g, '\\"')}"]`;

  const forms = document.querySelectorAll('form[action*="/cart/add"]');
  forms.forEach((form) => {
    const pidInput = form.querySelector('input[name="product-id"], input[name="id"]');
    let belongs = false;
    if (section && section.contains(form)) belongs = true;
    if (
      !belongs &&
      pidInput &&
      productId &&
      pidInput.value &&
      pidInput.value.toString().indexOf(productId.toString()) !== -1
    ) {
      belongs = true;
    }
    if (!belongs) return;

    const existing = form.querySelector(inputSel);
    if (!attach || !value) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      existing.value = value;
    } else {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.value = value;
      input.dataset.fromLineProperty = 'true';
      form.appendChild(input);
    }
  });
}

/**
 * Returns true when a `variant:update` event belongs to a different product
 * than the host (quick-add dialogs and the sticky bar can both fire it).
 *
 * @param {CustomEvent} event
 * @param {HTMLElement} host
 * @returns {boolean}
 */
export function isForeignVariantUpdate(event, host) {
  const eventProductId =
    event.detail?.data?.newProduct?.id ??
    (event.target instanceof HTMLElement ? event.target.dataset.productId : null);
  return Boolean(
    eventProductId &&
      host.dataset.productId &&
      eventProductId.toString() !== host.dataset.productId.toString()
  );
}

/**
 * Minimal host element for blocks whose only JS need is the cart line property.
 *
 * The value is product-level for current callers (pickup-only is a product
 * metafield, not a variant one), but Horizon re-renders the product form on
 * variant change, which drops any input we injected - so we re-sync on
 * `variant:update` regardless of whether the value itself changed.
 */
class AaaLineProperty extends HTMLElement {
  /** @type {Element | null} */
  section = null;

  connectedCallback() {
    this.section = closestSection(this);
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    syncLineProperty(this, this.section);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /** @param {Event} event */
  handleVariantUpdate = (event) => {
    if (isForeignVariantUpdate(/** @type {CustomEvent} */ (event), this)) return;
    syncLineProperty(this, this.section);
  };
}

if (!customElements.get('aaa-line-property')) {
  customElements.define('aaa-line-property', AaaLineProperty);
}
